/**
 * Capacitor AAALaunch plugin webview utilities.
 * Calls AAALaunch.webView() (camelCase) with the { link, style } shape
 * the native iOS (AAAPLaunchPlugin) and Android (LaunchPlugin) plugins expect.
 */

import { SharedConfig } from '../shared.config'
import { isNativeIOS } from './app-detect'

export interface LaunchWebviewCookieJar {
  domain: string
  name: string
  value: string
  /** Whether the cookie persists across sessions. @default 'true' */
  permanent?: string
}

export interface LaunchWebviewOptions {
  /** URL to open in the native webview. Required. */
  url: string
  /**
   * Style of the native webview container.
   * - 'standard'   – modal sheet with a close button (default)
   * - 'fullscreen' – full-screen presentation (no close/back option)
   * @default 'standard'
   */
  style?: 'standard' | 'fullscreen'
  /** Additional query parameters appended to the URL before loading. */
  params?: Record<string, string>
  /** Custom HTTP headers included in the initial page request. */
  headers?: Record<string, string>
  /** Cookies injected into the webview before loading the URL. */
  cookies?: LaunchWebviewCookieJar[]
  /** Enable HTTP response caching in the webview. @default false */
  enableCache?: boolean
  /**
   * Hide the host app's bottom tab/navigation bar while the webview is open.
   * Gives the webview more vertical space on devices with large bottom bars.
   * @default false
   */
  hideBottomNav?: boolean
  /**
   * Allow the user to swipe/slide the webview down to dismiss it (iOS only).
   * @default false
   */
  slideToDismiss?: boolean
  /** Clear all cookies from the webview session on open. @default false */
  deleteCookies?: boolean
  /** Prevent the plugin from appending analytics/tracking query params. @default false */
  disableTrackingParams?: boolean
  /**
   * URL schemes the webview is permitted to hand off to the native OS
   * (e.g. ['tel:', 'mailto:', 'maps:']).
   */
  allowedCustomSchemes?: string[]
  /**
   * Visual style of the close button inside the webview.
   * - 'default' – standard close/X icon
   * - 'mwg'     – MWG-branded close button
   * @default 'default'
   */
  closeButtonStyle?: 'default' | 'mwg'
}

declare global {
  interface Window {
    Capacitor?: {
      Plugins?: {
        AAALaunch?: {
          webView: (options: {
            link: {
              url: string
              params?: Record<string, string>
              options?: Record<string, unknown>
            }
            style: string
          }) => Promise<unknown>
          closeWebView: () => Promise<unknown>
        }
      }
    }
  }
}

export function isLaunchWebviewAvailable(): boolean {
  return typeof window !== 'undefined' && Boolean(
    (window as Window).Capacitor?.Plugins?.AAALaunch
  )
}

/**
 * Whether the tracker can use the native webview. Older iOS builds re-encode the URL query
 * (`%2B` becomes `+`) and break the tracker link, so iOS needs the host to opt in through
 * `iosWebviewSupported`; Android never had that bug.
 */
export function isLaunchWebviewSupported(
  config: Pick<SharedConfig, 'nativeAppView' | 'iosWebviewSupported'>
): boolean {
  if (!config.nativeAppView || !isLaunchWebviewAvailable()) {
    return false
  }
  return isNativeIOS() ? config.iosWebviewSupported === true : true
}

/** True from opening a webview until the native plugin reports it closed or `closeLaunchWebview` closes it. */
let webviewOpen = false
/**
 * Bumped on every open. Native dismisses a presented webview before showing a new one, so an older
 * open's promise can settle after a newer one started; only the latest open may clear `webviewOpen`.
 */
let lastOpenId = 0
/** True when the last webview was closed by `closeLaunchWebview` instead of the user. */
let closedProgrammatically = false

export function isLaunchWebviewOpen(): boolean {
  return webviewOpen
}

export function wasLaunchWebviewClosedProgrammatically(): boolean {
  return closedProgrammatically
}

/**
 * Closes the webview opened by `openLaunchWebview`. Does nothing when none is open: iOS
 * `closeWebView` dismisses whatever is presented on top, which could be another modal.
 * Resolves to whether a close was requested.
 */
export function closeLaunchWebview(): Promise<boolean> {
  const plugin = (window as Window).Capacitor?.Plugins?.AAALaunch
  if (!plugin?.closeWebView || !webviewOpen) {
    return Promise.resolve(false)
  }
  const openId = lastOpenId
  closedProgrammatically = true
  return plugin.closeWebView().then(
    () => {
      // iOS dismisses the webview without settling the open `webView()` call, so mark it closed here.
      if (openId === lastOpenId) {
        webviewOpen = false
      }
      return true
    },
    () => {
      closedProgrammatically = false
      return false
    }
  )
}

export function openLaunchWebview(options: LaunchWebviewOptions): Promise<void> {
  const plugin = (window as Window).Capacitor?.Plugins?.AAALaunch
  if (!plugin || !options?.url) {
    return Promise.resolve()
  }

  const {
    url, style, params, headers, cookies,
    enableCache, hideBottomNav, slideToDismiss, deleteCookies,
    disableTrackingParams, allowedCustomSchemes, closeButtonStyle,
  } = options

  const linkOptions: Record<string, unknown> = {}
  linkOptions['hideBottomNav'] = hideBottomNav !== undefined ? hideBottomNav : true
  if (headers)                           linkOptions['headers'] = headers
  if (cookies)                           linkOptions['cookies'] = cookies
  if (enableCache !== undefined)         linkOptions['enableCache'] = enableCache
  if (slideToDismiss !== undefined)      linkOptions['slideToDismiss'] = slideToDismiss
  if (deleteCookies !== undefined)       linkOptions['deleteCookies'] = deleteCookies
  if (disableTrackingParams !== undefined) linkOptions['disableTrackingParams'] = disableTrackingParams
  if (allowedCustomSchemes)             linkOptions['allowedCustomSchemes'] = allowedCustomSchemes
  if (closeButtonStyle)                 linkOptions['closeButtonStyle'] = closeButtonStyle

  const openId = ++lastOpenId
  const settle = () => {
    if (openId === lastOpenId) {
      webviewOpen = false
    }
  }
  webviewOpen = true
  closedProgrammatically = false
  return plugin.webView({
    link: {
      url,
      ...(params ? { params } : {}),
      ...(Object.keys(linkOptions).length ? { options: linkOptions } : {}),
    },
    style: style ?? 'standard',
  }).then(
    () => settle(),
    (error) => {
      settle()
      throw error
    }
  )
}
